/**
 * AdminTableFilters - Reusable filter components for admin list views
 * 
 * Features:
 * - Global search box
 * - Text filters (contains/starts with)
 * - Numeric filters (min/max)
 * - Date filters (from/to with quick presets)
 * - Status/enum multi-select
 * - Boolean filters
 * - Active filter chips
 * - Clear all filters
 */
import React, { useState, useCallback } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Search, 
  X, 
  Filter, 
  Calendar as CalendarIcon,
  ChevronDown
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

// ===================================================
// GLOBAL SEARCH
// ===================================================

export const GlobalSearch = ({ 
  value, 
  onChange, 
  placeholder = 'Search...', 
  className,
  'data-testid': testId = 'global-search'
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  
  // Debounced search
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // Debounce the actual search
    const timeoutId = setTimeout(() => {
      onChange?.(newValue);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [onChange]);
  
  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange?.('');
  }, [onChange]);
  
  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory-400" />
      <Input
        type="text"
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        className="pl-10 pr-8"
        data-testid={testId}
      />
      {localValue && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory-400 hover:text-ivory-600"
          data-testid={`${testId}-clear`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// ===================================================
// STATUS/ENUM FILTER
// ===================================================

export const StatusFilter = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = 'All statuses',
  label,
  className,
  'data-testid': testId = 'status-filter'
}) => {
  return (
    <div className={className}>
      {label && <label className="block text-sm text-ivory-600 mb-1">{label}</label>}
      <Select value={value || 'all'} onValueChange={onChange}>
        <SelectTrigger className="w-full" data-testid={testId}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{placeholder}</SelectItem>
          {options.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// ===================================================
// MULTI-SELECT STATUS FILTER
// ===================================================

export const MultiStatusFilter = ({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select statuses',
  label,
  className,
  'data-testid': testId = 'multi-status-filter'
}) => {
  const selectedLabels = value.length > 0
    ? value.map(v => options.find(o => o.value === v)?.label || v).join(', ')
    : placeholder;
  
  const handleToggle = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange?.(newValue);
  };
  
  return (
    <div className={className}>
      {label && <label className="block text-sm text-ivory-600 mb-1">{label}</label>}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-between font-normal"
            data-testid={testId}
          >
            <span className="truncate">{selectedLabels}</span>
            <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="start">
          {options.map(opt => (
            <DropdownMenuCheckboxItem
              key={opt.value}
              checked={value.includes(opt.value)}
              onCheckedChange={() => handleToggle(opt.value)}
            >
              {opt.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// ===================================================
// DATE RANGE FILTER
// ===================================================

const DATE_PRESETS = [
  { label: 'Today', getValue: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  { label: 'Last 7 days', getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
  { label: 'Last 30 days', getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { label: 'Last 90 days', getValue: () => ({ from: subDays(new Date(), 90), to: new Date() }) },
];

export const DateRangeFilter = ({
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  label,
  showPresets = true,
  className,
  'data-testid': testId = 'date-range-filter'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const displayValue = fromValue || toValue
    ? `${fromValue ? format(new Date(fromValue), 'MMM d') : '...'} - ${toValue ? format(new Date(toValue), 'MMM d') : '...'}`
    : 'Select dates';
  
  const handlePreset = (preset) => {
    const { from, to } = preset.getValue();
    onFromChange?.(from.toISOString());
    onToChange?.(to.toISOString());
  };
  
  const handleClear = () => {
    onFromChange?.(null);
    onToChange?.(null);
  };
  
  return (
    <div className={className}>
      {label && <label className="block text-sm text-ivory-600 mb-1">{label}</label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start font-normal"
            data-testid={testId}
          >
            <CalendarIcon className="w-4 h-4 mr-2 text-ivory-400" />
            {displayValue}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 space-y-3">
            {showPresets && (
              <div className="flex flex-wrap gap-2 pb-3 border-b">
                {DATE_PRESETS.map(preset => (
                  <Button
                    key={preset.label}
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreset(preset)}
                    className="text-xs"
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-ivory-500">From</label>
                <Calendar
                  mode="single"
                  selected={fromValue ? new Date(fromValue) : undefined}
                  onSelect={(date) => onFromChange?.(date?.toISOString() || null)}
                  initialFocus
                />
              </div>
              <div>
                <label className="text-xs text-ivory-500">To</label>
                <Calendar
                  mode="single"
                  selected={toValue ? new Date(toValue) : undefined}
                  onSelect={(date) => onToChange?.(date?.toISOString() || null)}
                />
              </div>
            </div>
            
            {(fromValue || toValue) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="w-full text-ivory-500"
              >
                Clear dates
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// ===================================================
// NUMERIC RANGE FILTER
// ===================================================

export const NumericRangeFilter = ({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  label,
  placeholder = { min: 'Min', max: 'Max' },
  prefix = '',
  className,
  'data-testid': testId = 'numeric-range-filter'
}) => {
  return (
    <div className={className}>
      {label && <label className="block text-sm text-ivory-600 mb-1">{label}</label>}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory-400 text-sm">
              {prefix}
            </span>
          )}
          <Input
            type="number"
            value={minValue || ''}
            onChange={(e) => onMinChange?.(e.target.value || null)}
            placeholder={placeholder.min}
            className={prefix ? 'pl-7' : ''}
            data-testid={`${testId}-min`}
          />
        </div>
        <span className="text-ivory-400">—</span>
        <div className="relative flex-1">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory-400 text-sm">
              {prefix}
            </span>
          )}
          <Input
            type="number"
            value={maxValue || ''}
            onChange={(e) => onMaxChange?.(e.target.value || null)}
            placeholder={placeholder.max}
            className={prefix ? 'pl-7' : ''}
            data-testid={`${testId}-max`}
          />
        </div>
      </div>
    </div>
  );
};

// ===================================================
// BOOLEAN FILTER
// ===================================================

export const BooleanFilter = ({
  value,
  onChange,
  label,
  trueLabel = 'Yes',
  falseLabel = 'No',
  allLabel = 'All',
  className,
  'data-testid': testId = 'boolean-filter'
}) => {
  return (
    <div className={className}>
      {label && <label className="block text-sm text-ivory-600 mb-1">{label}</label>}
      <Select value={value?.toString() || 'all'} onValueChange={(v) => onChange?.(v === 'all' ? null : v === 'true')}>
        <SelectTrigger data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{allLabel}</SelectItem>
          <SelectItem value="true">{trueLabel}</SelectItem>
          <SelectItem value="false">{falseLabel}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

// ===================================================
// ACTIVE FILTER CHIPS
// ===================================================

export const ActiveFilterChips = ({
  filters = {},
  filterLabels = {},
  onRemove,
  onClearAll,
  className
}) => {
  const activeFilters = Object.entries(filters).filter(([_, value]) => {
    if (value === null || value === undefined || value === '' || value === 'all') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
  
  if (activeFilters.length === 0) return null;
  
  const getFilterDisplay = (key, value) => {
    const label = filterLabels[key] || key;
    if (typeof value === 'boolean') {
      return `${label}: ${value ? 'Yes' : 'No'}`;
    }
    if (Array.isArray(value)) {
      return `${label}: ${value.length} selected`;
    }
    return `${label}: ${value}`;
  };
  
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {activeFilters.map(([key, value]) => (
        <Badge
          key={key}
          variant="secondary"
          className="gap-1 pr-1"
        >
          {getFilterDisplay(key, value)}
          <button
            onClick={() => onRemove?.(key)}
            className="ml-1 hover:bg-ivory-300 rounded-full p-0.5"
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      ))}
      
      {activeFilters.length > 1 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="text-ivory-500 h-6 px-2"
        >
          Clear all
        </Button>
      )}
    </div>
  );
};

// ===================================================
// FILTER BAR CONTAINER
// ===================================================

export const FilterBar = ({
  children,
  onClearAll,
  hasActiveFilters,
  className
}) => {
  return (
    <div className={cn('bg-white rounded-xl border border-ivory-200 p-4 shadow-soft', className)}>
      <div className="flex flex-col sm:flex-row gap-4">
        {children}
      </div>
      
      {hasActiveFilters && onClearAll && (
        <div className="mt-3 pt-3 border-t border-ivory-100 flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="text-ivory-500"
          >
            <X className="w-4 h-4 mr-1" />
            Clear all filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
